import { useMemo } from "react";

import { HOTEL_NAME } from "@/constants/hotels";

import { FilterOptions, FilterSourceData, OptionItem } from "../typing";
import { getBanksByBrand } from "../utils";

const transformerOptions = (map: Map<string, string>): OptionItem[] => {
  const options = [];
  for (const [value, label] of map.entries()) {
    options.push({ label, value });
  }

  return options;
};

const useBrandOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const brandOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const brand = list[index].brand;
      brandOptionsMap.set(brand, HOTEL_NAME[brand]);
    }
    return transformerOptions(brandOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [list]);
};
const useSubBrandOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const subBrandOptionsMap = new Map<string, string>();
    for (let index = 0; index < list.length; index++) {
      const subBrand = list[index].sub_brand;
      if (subBrand) {
        subBrandOptionsMap.set(subBrand, subBrand);
      }
    }
    return transformerOptions(subBrandOptionsMap).sort((a, b) => a.label?.localeCompare(b.label));
  }, [list]);
};

const useBanksOptions = (options: OptionItem[]): OptionItem[] => {
  return useMemo(() => {
    const banksOptionsMap = new Map<string, string>();
    for (let index = 0; index < options.length; index++) {
      const brandCode = options[index].value;

      getBanksByBrand(brandCode).forEach((bank) => {
        banksOptionsMap.set(bank, bank);
      });
    }

    return transformerOptions(banksOptionsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [options]);
};

const usePointsPriceRange = (list: FilterSourceData): number[] => {
  return useMemo(() => {
    const data = list.map((item) => item?.book_info?.points_rate || 0).sort((a, b) => a - b);

    if (data && data.length > 0) {
      return [Number(data[0]), Number(data[data.length - 1])];
    } else {
      return [];
    }
  }, [list]);
};

const useDistanceRange = (list: FilterSourceData): number[] => {
  return useMemo(() => {
    const data = list.map((item) => item?.book_info?.distance || 0).sort((a, b) => a - b);

    if (data && data.length > 0) {
      return [Number(data[0]), Number(data[data.length - 1])];
    } else {
      return [];
    }
  }, [list]);
};

const useCashRateRange = (list: FilterSourceData): number[] => {
  return useMemo(() => {
    const data = list.map((item) => item?.book_info?.cash_rate || 0).sort((a, b) => a - b);

    if (data && data.length > 0) {
      return [Number(data[0]), Number(data[data.length - 1])];
    } else {
      return [];
    }
  }, [list]);
};

const useGRatingRange = (list: FilterSourceData): number[] => {
  return useMemo(() => {
    const data = list.map((item) => item?.g_rating || 0).sort((a, b) => a - b);

    if (data && data.length > 0) {
      return [Number(data[0]), Number(data[data.length - 1])];
    } else {
      return [];
    }
  }, [list]);
};

const useCertificatesOptions = (list: FilterSourceData): OptionItem[] => {
  return useMemo(() => {
    const opts = Array.from(new Set(list.map((item) => item.book_info?.certificates).flat()));
    return opts.map((v) => ({ label: v, value: v })) as OptionItem[];
  }, [list]);
};

/**
 * 根据数据列表获取筛选项
 */
export const useFilterOptions = (list: FilterSourceData): FilterOptions => {
  const brandOptions = useBrandOptions(list);
  const subBrandOptions = useSubBrandOptions(list);
  const banksOptions = useBanksOptions(brandOptions);
  const pointsPriceRange = usePointsPriceRange(list);
  const distanceRange = useDistanceRange(list);
  const cashRateRange = useCashRateRange(list);
  const gRatingRange = useGRatingRange(list);
  const certificates = useCertificatesOptions(list);

  return {
    brandOptions,
    banksOptions,
    subBrandOptions,
    pointsPriceRange,
    distanceRange,
    cashRateRange,
    gRatingRange,
    certificates,
  };
};
